import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { brl, dt } from "@/lib/format";
import { SUPPORT_EMAIL } from "@/lib/support";

export default function PixDialog({ pix, onClose, onPaid }) {
  const [p, setP] = useState(pix);
  useEffect(() => { setP(pix); }, [pix]);
  useEffect(() => {
    if (!pix || p?.status !== "pending") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/billing/pix/${pix.id}`);
        setP(data);
        if (data.status === "approved") { toast.success("Pix aprovado! Seu plano já está ativo."); onPaid(); }
      } catch (e) { console.warn("Pix status check failed, retrying", e); }
    }, 5000);
    return () => clearInterval(t);
  }, [pix, p?.status, onPaid]);
  if (!p) return null;
  const copy = async () => { await navigator.clipboard.writeText(p.qr_code); toast.success("Código Pix copiado"); };
  const paid = p.status === "approved";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pix-dialog">
        <DialogHeader>
          <DialogTitle>Pagar com Pix</DialogTitle>
          <DialogDescription>Plano {p.plan_name} · {brl(p.amount)} · 31 dias de acesso</DialogDescription>
        </DialogHeader>
        {paid ? (
          <div className="py-8 text-center space-y-2" data-testid="pix-paid"><CheckCircle2 className="h-12 w-12 mx-auto text-[#34A853]" /><p className="font-semibold">Pagamento confirmado</p></div>
        ) : p.status !== "pending" ? (
          <p className="py-6 text-sm text-center text-rose-500" data-testid="pix-failed">Este Pix não está mais disponível ({p.status}). Gere um novo código. Se você já pagou, fale com <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
        ) : (
          <div className="space-y-4">
            {p.qr_code_base64 && <img src={`data:image/png;base64,${p.qr_code_base64}`} alt="QR Code Pix" className="w-56 h-56 mx-auto rounded-lg border" data-testid="pix-qr-image" />}
            <div className="flex gap-2"><input readOnly value={p.qr_code || ""} className="flex-1 min-w-0 h-10 px-3 rounded-md border bg-muted text-xs font-mono" data-testid="pix-copy-paste-code" />
              <Button onClick={copy} variant="outline" data-testid="pix-copy-button"><Copy className="h-4 w-4 mr-1" />Copiar</Button></div>
            <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="pix-waiting"><Loader2 className="h-3.5 w-3.5 animate-spin" />Aguardando confirmação do Mercado Pago{p.expires_at ? ` · válido até ${dt(p.expires_at)}` : ""}</p>
            <p className="text-xs text-muted-foreground" data-testid="pix-support">Problemas com o pagamento? <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
